import SEO from "@dogstrust/dt-web-core-lib-front-end/src/components/seo";
import IntroHeroWithSubheading from "@dogstrust/dt-web-core-lib-front-end/src/containers/introBlocks/IntroHeroWithSubheading";
import SectionHelpAdviceNewsletterPromo from "@dogstrust/dt-web-core-lib-front-end/src/containers/sections/SectionHelpAdviceNewsletterPromo";
import { SectionQuickLinksEmbedded } from "@dogstrust/dt-web-core-lib-front-end/src/containers/sections/SectionQuickLinks/SectionQuickLinks";
import SectionStandardSignPostCards from "@dogstrust/dt-web-core-lib-front-end/src/containers/sections/SectionStandardSignPostCards";
import SectionToDisplay from "@dogstrust/dt-web-core-lib-front-end/src/containers/sections/SectionToDisplay/SectionToDisplay";
import { usePageManager } from "@dogstrust/dt-web-core-lib-front-end/src/context";
import { Layout } from "@dogstrust/uk/components/Layout/Layout";

import {
	mapContentUnionToQuickLinksProps,
	mapContentUnionToSignPostCardProps,
} from "@dogstrust/dt-web-core-lib-front-end/src/utils/mappers";
import { getSeoProps } from "@dogstrust/dt-web-core-lib-front-end/src/utils/seo";

import { Head as DefaultHead } from "@dogstrust/dt-web-core-lib-front-end/src/components/Head/Head";
import React, { useEffect } from "react";

export interface DogIndexPageProps {
	data: Queries.AdvicePageQuery;
}

export const Head: React.FC<DogIndexPageProps> = ({
	data: {
		pageData: { field_meta_tags, field_hreflang_url },
	},
}) => {
	return (
		<DefaultHead pageUrl={"/dog-advice"} hrefLangUrl={field_hreflang_url}>
			<SEO
				{...getSeoProps(field_meta_tags, {
					title: "Dog Advice | Dogs Trust",
					description:
						"Help and advice on dog training, behaviour, care and our Dog School from our experts.",
					canonical_url: `${process.env.GATSBY_DOMAIN_NAME}/dog-advice`,
					og_type: "article",
					og_url: `${process.env.GATSBY_DOMAIN_NAME}/dog-advice`,
					og_title: "Dog Advice | Dogs Trust",
					og_description:
						"Help and advice on dog training, behaviour, care and our Dog School from our experts.",
					twitter_cards_type: "summary",
					twitter_cards_description:
						"Help and advice on dog training, behaviour, care and our Dog School from our experts.",
					twitter_cards_site: "@DogsTrust",
					twitter_cards_title: "Dog Advice | Dogs Trust",
					twitter_cards_page_url: `${process.env.GATSBY_DOMAIN_NAME}/dog-advice`,
				})}
			/>
		</DefaultHead>
	);
};

const AdviceLandingPage: React.FC<DogIndexPageProps> = ({
	data: {
		helpAdviceTopics: { nodes: allTopics },
		helpAdviceChildLandingPages: { nodes: allLandingPages },
		newsletterPromo,
		suggestedArticles,
		pageData: { breadcrumbs },
	},
}) => {
	const allQuickLinks = [...allTopics, ...allLandingPages];
	const { setContentWidth, deregisterPage, usePagination } = usePageManager();
	useEffect(() => {
		usePagination(false);
		setContentWidth("DEFAULT");
		return () => deregisterPage();
	}, [setContentWidth, deregisterPage, usePagination]);
	return (
		<Layout breadcrumbs={breadcrumbs ? [...breadcrumbs] : []}>
			<IntroHeroWithSubheading
				title="Dog Advice"
				subHeading="Help and advice on dog training, behaviour, care and our Dog School from our experts."
			/>
			<SectionQuickLinksEmbedded
				heading={"Explore by topic"}
				minItems={3}
				maxItems={12}
				theme="alternative"
				items={allQuickLinks
					.sort((a, b) => a.field_priority_weight - b.field_priority_weight)
					.map(mapContentUnionToQuickLinksProps)}
				withSearch={{
					name: "advice-search-input",
					title: "How can we help you?",
					placeholder: "Search all help topics",
					baseSearchUrl: "/dog-advice/search",
					value: "",
					justInput: false,
				}}
			/>
			<SectionToDisplay
				section={{
					...suggestedArticles,
					title: "Suggested articles",
				}}
				displayOption="accordion"
				withPageBreak={false}
			/>
			<SectionStandardSignPostCards
				title="Explore by topic"
				items={allQuickLinks.map(mapContentUnionToSignPostCardProps)}
			/>
			<SectionHelpAdviceNewsletterPromo
				title={newsletterPromo.field_front_end_title}
				body={newsletterPromo.field_section_short_description.value}
				buttonText={newsletterPromo.field_link.title}
				buttonUrl={newsletterPromo.field_link.uri}
			/>
		</Layout>
	);
};
export default AdviceLandingPage;
